import { useEffect, useRef } from "react";
import {
  Mic,
  MicOff,
  MonitorSpeaker,
  Phone,
  PhoneOff,
  Video,
  VideoOff,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ActiveCall } from "@/hooks/useCalls";

interface CallExperienceProps {
  incomingCall: ActiveCall | null;
  activeCall: ActiveCall | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isCameraEnabled: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function StreamVideo({
  stream,
  muted = false,
  className,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  className: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return <video ref={videoRef} autoPlay playsInline muted={muted} className={className} />;
}

export function CallExperience({
  incomingCall,
  activeCall,
  localStream,
  remoteStream,
  isMuted,
  isCameraEnabled,
  onAccept,
  onDecline,
  onEnd,
  onToggleMute,
  onToggleCamera,
}: CallExperienceProps) {
  const visibleCall = activeCall || incomingCall;

  if (!visibleCall) {
    return null;
  }

  const showIncoming = !activeCall && incomingCall;
  const isVideoCall = visibleCall.callType === "video";

  return (
    <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm">
      <div className="flex h-full flex-col p-4 md:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="space-y-2">
            <Badge className="bg-primary text-primary-foreground">
              {showIncoming ? "Incoming call" : visibleCall.status === "active" ? "Live call" : "Connecting"}
            </Badge>
            <div>
              <h2 className="text-2xl font-semibold text-foreground md:text-3xl">{visibleCall.partnerName}</h2>
              <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">{visibleCall.partnerRole}</p>
            </div>
          </div>
          <div className="hidden rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground md:block">
            {visibleCall.callType === "video" ? "Secure video room" : "Secure audio room"}
          </div>
        </div>

        <div className="grid flex-1 gap-4 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card">
            {isVideoCall && remoteStream ? (
              <StreamVideo stream={remoteStream} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-card to-secondary/40 px-6 text-center">
                <Avatar className="h-24 w-24 border border-border shadow-lg md:h-28 md:w-28">
                  <AvatarFallback className="bg-primary/10 text-2xl font-semibold text-primary">
                    {getInitials(visibleCall.partnerName)}
                  </AvatarFallback>
                </Avatar>
                <h3 className="mt-6 text-2xl font-semibold text-foreground">{visibleCall.partnerName}</h3>
                <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">
                  {showIncoming
                    ? "Someone is reaching you through PropTRUST messaging."
                    : visibleCall.status === "active"
                      ? "Call is live. Continue the transaction conversation here."
                      : "We are connecting both parties and securing the media channel."}
                </p>
              </div>
            )}

            {activeCall && isVideoCall ? (
              <div className="absolute bottom-4 right-4 h-36 w-28 overflow-hidden rounded-3xl border border-white/10 bg-foreground shadow-2xl md:h-44 md:w-32">
                {localStream && isCameraEnabled ? (
                  <StreamVideo stream={localStream} muted className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center bg-muted text-muted-foreground">
                    <VideoOff className="h-5 w-5" />
                    <span className="mt-2 text-[11px] uppercase tracking-[0.2em]">Camera off</span>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-[2rem] border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3">
                  {isVideoCall ? <MonitorSpeaker className="h-5 w-5 text-primary" /> : <Phone className="h-5 w-5 text-primary" />}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">PropTRUST Call Controls</h3>
                  <p className="text-sm text-muted-foreground">
                    {showIncoming
                      ? "Answer from here or decline without leaving the dashboard."
                      : "Mute, toggle camera, or end the call without interrupting the rest of the workspace."}
                  </p>
                </div>
              </div>

              {showIncoming ? (
                <div className="mt-6 flex flex-col gap-3">
                  <Button size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={onAccept}>
                    <Phone className="mr-2 h-4 w-4" />
                    Answer call
                  </Button>
                  <Button size="lg" variant="outline" className="w-full" onClick={onDecline}>
                    <PhoneOff className="mr-2 h-4 w-4" />
                    Decline
                  </Button>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <Button variant="outline" className="h-14 rounded-2xl" onClick={onToggleMute}>
                    {isMuted ? <MicOff className="mr-2 h-4 w-4" /> : <Mic className="mr-2 h-4 w-4" />}
                    {isMuted ? "Unmute" : "Mute"}
                  </Button>
                  {isVideoCall ? (
                    <Button variant="outline" className="h-14 rounded-2xl" onClick={onToggleCamera}>
                      {isCameraEnabled ? <Video className="mr-2 h-4 w-4" /> : <VideoOff className="mr-2 h-4 w-4" />}
                      {isCameraEnabled ? "Camera on" : "Camera off"}
                    </Button>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-4 py-3 text-center text-sm text-muted-foreground">
                      Audio-only call
                    </div>
                  )}
                  <Button
                    className="col-span-2 h-14 rounded-2xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={onEnd}
                  >
                    <PhoneOff className="mr-2 h-4 w-4" />
                    End call
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-[2rem] border border-border bg-card p-6">
              <h3 className="font-semibold text-foreground">Call status</h3>
              <div className="mt-4 space-y-3">
                <div className="rounded-2xl bg-muted/40 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Direction</p>
                  <p className="mt-1 text-sm font-medium capitalize text-foreground">{visibleCall.direction}</p>
                </div>
                <div className="rounded-2xl bg-muted/40 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Mode</p>
                  <p className="mt-1 text-sm font-medium capitalize text-foreground">{visibleCall.callType}</p>
                </div>
                <div className="rounded-2xl bg-muted/40 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">State</p>
                  <p className="mt-1 text-sm font-medium capitalize text-foreground">{visibleCall.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
